/**
 * features/hr/NewLeaveApplication.tsx
 * ------------------------------------------------------------------
 * HR LEAVE — the modernised "new application" modal, raised from the
 * Leave hub. Pick the staff member, leave type and dates; the form
 * shows the live duration and a prominent remaining-balance preview
 * for that person + type (resolved through the cohort policy), warning
 * if the request would exceed the balance. Submits a pending request.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Check, CalendarDays, AlertTriangle } from 'lucide-react';
import { glassFieldClass } from '@/features/finance/ui';
import { FormModal, modalPrimaryBtn, modalGhostBtn } from '@/components/ui/glass';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import StaffPicker from '@/features/hr/StaffPicker';
import { useHrData } from '@/features/hr/useHrData';
import { useLeavePolicies } from '@/features/hr/useLeavePolicies';
import { hr } from '@/features/hr/hrWrite';
import { LEAVE_TYPE_LABELS, type LeaveType } from '@/lib/hr';
import { leaveBalancesFor, currentLeaveYear, ENTITLEMENT_LEAVE_TYPES } from '@/lib/leavePolicy';
import { countLeaveDays } from '@/lib/hrLeave';
import { usePublicHolidays } from '@/features/hr/usePublicHolidays';
import { salesFor } from '@/lib/leaveEncashment';

export default function NewLeaveApplication({
  open,
  onClose,
  presetStaffId,
}: {
  open: boolean;
  onClose: () => void;
  presetStaffId?: string;
}) {
  const data = useHrData();
  const { policies } = useLeavePolicies();
  const { isHoliday } = usePublicHolidays();
  const today = new Date().toISOString().slice(0, 10);
  const [staffId, setStaffId] = useState(presetStaffId ?? '');
  const [type, setType] = useState<LeaveType>('annual');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [reason, setReason] = useState('');
  const [save, setSave] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });

  const days = useMemo(
    () => countLeaveDays(startDate, endDate, isHoliday, type),
    [startDate, endDate, isHoliday, type],
  );

  const staff = data.staff.find((s) => s.id === staffId);
  const balanceForType = useMemo(() => {
    if (!staff) return undefined;
    const allocation = data.leaveAllocations.find((a) => a.staffId === staff.id);
    const requests = data.leave.filter((l) => l.staffId === staff.id);
    /* Sold days are gone. Without them a teacher could sell five days and
       then apply for the same five as leave, inside a balance that no
       longer holds them. */
    const balances = leaveBalancesFor({ category: staff.category, allocation, requests, policies, year: currentLeaveYear(), accrualStart: data.payrollSettings?.leaveAccrualStart, employmentStart: staff.startDate, encashments: salesFor(data.leaveEncashments, staff.id) });
    return balances.find((b) => b.type === type);
  }, [staff, data.leaveAllocations, data.leave, data.leaveEncashments, policies, type]);

  const isEntitled = (ENTITLEMENT_LEAVE_TYPES as readonly string[]).includes(type);
  const remainingAfter = balanceForType ? balanceForType.remaining - days : undefined;
  const wouldExceed = isEntitled && remainingAfter !== undefined && remainingAfter < 0;
  const canSave = !!staffId && !!startDate && !!endDate && days > 0;

  async function submit() {
    if (!canSave) return;
    setSave({ show: true, status: 'saving', message: 'Submitting leave application' });
    try {
      await hr.leave.add({
        staffId,
        type,
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
        days,
        reason: reason.trim(),
        status: 'pending',
      });
      setSave({ show: true, status: 'success', message: 'Leave application submitted.' });
      // Clear the form so the next application starts blank.
      setStaffId(presetStaffId ?? '');
      setType('annual');
      setStartDate(today);
      setEndDate(today);
      setReason('');
      onClose();
    } catch (e) {
      setSave({ show: true, status: 'error', message: e instanceof Error ? e.message : 'Could not submit.' });
    }
  }

  return (
    <>
      <SaveConfirmation show={save.show} status={save.status} message={save.message} onDismiss={() => setSave((s) => ({ ...s, show: false }))} />
      <FormModal
        open={open}
        onClose={onClose}
        icon={CalendarDays}
        title="New leave application"
        footer={
          <>
            <button onClick={onClose} className={modalGhostBtn}>Cancel</button>
            <button onClick={submit} disabled={!canSave} className={modalPrimaryBtn}>
              <Check size={15} /> Submit application
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Staff member</label>
            <div className="mt-1">
              <StaffPicker staff={data.staff} value={staffId} onChange={setStaffId} activeOnly />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Leave type</label>
            <select value={type} onChange={(e) => setType(e.target.value as LeaveType)} className={glassFieldClass('mt-1 w-full')}>
              {(Object.keys(LEAVE_TYPE_LABELS) as LeaveType[]).map((t) => (
                <option key={t} value={t}>{LEAVE_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Duration</label>
            <div className="mt-1 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 text-sm">
              <span className="text-2xl font-bold tabular-nums text-gray-900 dark:text-white">{days}</span>
              <span className="text-gray-500 dark:text-gray-400"> day{days === 1 ? '' : 's'}</span>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300">From</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={glassFieldClass('mt-1 w-full')} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300">To</label>
            <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className={glassFieldClass('mt-1 w-full')} />
          </div>

          {/* Live balance preview */}
          {staff && isEntitled && balanceForType && (
            <div className={`sm:col-span-2 rounded-xl border p-3 ${wouldExceed ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40'}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {LEAVE_TYPE_LABELS[type]} balance for {staff.firstName} {staff.lastName}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">{balanceForType.remaining}</div>
                    <div className="text-[11px] text-gray-400">remaining</div>
                  </div>
                  <div className="text-gray-300">→</div>
                  <div className="text-center">
                    <div className={`text-lg font-bold tabular-nums ${wouldExceed ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>{remainingAfter}</div>
                    <div className="text-[11px] text-gray-400">after this</div>
                  </div>
                </div>
              </div>
              {wouldExceed && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                  <AlertTriangle size={13} /> This exceeds the remaining balance — the extra days would be unpaid / need approval.
                </div>
              )}
            </div>
          )}

          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Reason (optional)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className={glassFieldClass('mt-1 w-full')} />
          </div>
        </div>
      </FormModal>
    </>
  );
}
